const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const CRM_API_URL = process.env.REACT_APP_CRM_API_URL || 'http://localhost:5001/api/crm';

/**
 * Anexa o status HTTP ao corpo, sem mudar a forma do retorno.
 *
 * A criação de cotação tem DUAS barreiras que respondem com corpo JSON e
 * status de erro: 422 `data_no_passado` e 409 `decisao_de_oportunidade`.
 * Como `res.json()` descarta o status, o chamador não tinha como
 * distinguir "recusado, pergunte ao vendedor" de "criado" — e seguia para
 * `navigate(/app/cotacao/${quote.id})` com id indefinido.
 *
 * Anexar em vez de trocar por `{ ok, status, data }` é deliberado: quatro
 * arquivos consomem estas funções lendo `quote.id` direto. Trocar a forma
 * obrigaria a mexer nos quatro; anexar mantém todos funcionando e dá o
 * status a quem precisa dele.
 */
async function withStatus(res) {
  let corpo = null;
  try {
    corpo = await res.json();
  } catch (e) {
    corpo = {};
  }
  return { ...(corpo || {}), __status: res.status, __ok: res.ok };
}

export const api = {
  // ─── Contato CRM (busca nome da nascente get_display_name) ───
  async getContact(contactId) {
    const res = await fetch(`${CRM_API_URL}/contacts/${contactId}`);
    if (!res.ok) return null;
    return res.json();
  },

  async createQuote(data) {
    const res = await fetch(`${API_URL}/quotes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  },

  async getQuotes(sellerId = null, page = 1, perPage = 10) {
    const params = new URLSearchParams({ page, per_page: perPage });
    if (sellerId) {
      params.append('seller_id', sellerId);
    }
    const res = await fetch(`${API_URL}/quotes?${params}`);
    return res.json();
  },

  async getQuote(id) {
    const res = await fetch(`${API_URL}/quotes/${id}`);
    return res.json();
  },

  async deleteQuote(id) {
    const res = await fetch(`${API_URL}/quotes/${id}`, {
      method: 'DELETE'
    });
    return res.json();
  },

  async addBlock(quoteId, data) {
    const res = await fetch(`${API_URL}/quotes/${quoteId}/blocks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  },

  async editBlock(quoteId, blockId, data) {
    const res = await fetch(`${API_URL}/quotes/${quoteId}/blocks/${blockId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  },

  async removeBlock(quoteId, blockId) {
    const res = await fetch(`${API_URL}/quotes/${quoteId}/blocks/${blockId}`, {
      method: 'DELETE'
    });
    return res.json();
  },

  async publishQuote(quoteId) {
    const res = await fetch(`${API_URL}/quotes/${quoteId}/publish`, {
      method: 'POST'
    });
    return res.json();
  },

  async viewQuote(publicLink) {
    const res = await fetch(`${API_URL}/c/${publicLink}`);
    return res.json();
  },

  /**
   * Extrai dados de arquivos de viagem usando processamento assíncrono.
   */
  async extractQuote(files, onProgress = null) {
    const formData = new FormData();

    if (Array.isArray(files)) {
      files.forEach(file => formData.append('file', file));
    } else {
      formData.append('file', files);
    }

    const createRes = await fetch(`${API_URL}/quotes/extract`, {
      method: 'POST',
      body: formData
    });

    const createData = await createRes.json();

    if (!createData.job_id) {
      throw new Error(createData.error || 'Erro ao criar job de extração');
    }

    const jobId = createData.job_id;
    const maxAttempts = 120;
    const pollInterval = 2000;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const statusRes = await fetch(`${API_URL}/jobs/${jobId}`);
      const statusData = await statusRes.json();

      if (onProgress) {
        onProgress(statusData.status);
      }

      if (statusData.status === 'completed') {
        return statusData.result;
      }

      if (statusData.status === 'failed') {
        throw new Error(statusData.error || 'Extração falhou');
      }

      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }

    throw new Error('Timeout: extração demorou muito');
  },

  async getJobStatus(jobId) {
    const res = await fetch(`${API_URL}/jobs/${jobId}`);
    return res.json();
  },

  async assistantExtract(prompt, files) {
    const formData = new FormData();
    formData.append('prompt', prompt);

    files.forEach(file => formData.append('file', file));

    const res = await fetch(`${API_URL}/assistant/extract`, {
      method: 'POST',
      body: formData
    });

    return res.json();
  },

  async assistantRefine(currentData, correction) {
    const res = await fetch(`${API_URL}/assistant/refine`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        current_data: currentData,
        correction: correction
      })
    });

    return res.json();
  },

  async aiCorrect(quoteId, instruction) {
    const res = await fetch(`${API_URL}/quotes/${quoteId}/ai-correct`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ instruction })
    });
    return res.json();
  },

  async deriveQuote(quoteId, optionName = '') {
    const res = await fetch(`${API_URL}/quotes/${quoteId}/derive`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ option_name: optionName })
    });
    return res.json();
  },

  async createQuoteFromExtracted(data) {
    const res = await fetch(`${API_URL}/quotes/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return withStatus(res);
  },

  async createQuoteFromTemplate(template, data = {}) {
    const res = await fetch(`${API_URL}/quotes/template`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ template, ...data })
    });
    return withStatus(res);
  },

  async reprocessQuote(quoteId, files) {
    const formData = new FormData();

    if (Array.isArray(files)) {
      files.forEach(file => formData.append('file', file));
    } else {
      formData.append('file', files);
    }

    const res = await fetch(`${API_URL}/quotes/${quoteId}/reprocess`, {
      method: 'POST',
      body: formData
    });

    return res.json();
  },

  async updateMarkup(quoteId, markup) {
    const res = await fetch(`${API_URL}/quotes/${quoteId}/markup`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ markup })
    });
    return res.json();
  },

  async getDestinationImages(destination) {
    const res = await fetch(
      `${API_URL}/destinations/images?destination=${encodeURIComponent(destination)}`
    );
    return res.json();
  },

  async saveDestinationImages(destination, imageUrl1, imageUrl2) {
    const res = await fetch(`${API_URL}/destinations/images`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        destination,
        destination_image_url: imageUrl1,
        destination_image_url_2: imageUrl2
      })
    });
    return res.json();
  },

  async getHotelImages(hotelName) {
    const res = await fetch(
      `${API_URL}/hotels/images?name=${encodeURIComponent(hotelName)}`
    );
    return res.json();
  },

  async getHotelsImagesBatch(hotelNames) {
    const res = await fetch(`${API_URL}/hotels/images/batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ names: hotelNames })
    });
    return res.json();
  },

  async saveHotelImages(hotelName, imageUrl, imageUrl2, imageUrl3) {
    const res = await fetch(`${API_URL}/hotels/images`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: hotelName,
        image_url: imageUrl,
        image_url_2: imageUrl2,
        image_url_3: imageUrl3
      })
    });
    return res.json();
  },

  async saveHotelsImagesBatch(hotels) {
    const res = await fetch(`${API_URL}/hotels/images/batch`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hotels })
    });
    return res.json();
  },

  async getCarsImagesBatch(carNames) {
    const res = await fetch(`${API_URL}/cars/images/batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ names: carNames })
    });
    return res.json();
  },

  async saveCarsImagesBatch(cars) {
    const res = await fetch(`${API_URL}/cars/images/batch`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cars })
    });
    return res.json();
  },

  async getToursImagesBatch(tourNames) {
    const res = await fetch(`${API_URL}/tours/images/batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ names: tourNames })
    });
    return res.json();
  },

  async saveToursImagesBatch(tours) {
    const res = await fetch(`${API_URL}/tours/images/batch`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tours })
    });
    return res.json();
  },

  async getStorytelling(quoteId) {
    const res = await fetch(`${API_URL}/quotes/${quoteId}/storytelling`);
    return res.json();
  },

  // ─── Engagement ───

  async getEngagementBulk(quotationIds) {
    if (!quotationIds.length) return {};
    const ids = quotationIds.join(',');
    const res = await fetch(`${API_URL}/engagement/quotes?ids=${ids}`);
    return res.json();
  },

  async getEngagementDashboard() {
    const res = await fetch(`${API_URL}/engagement/dashboard`);
    return res.json();
  },

  async getEngagementThresholds() {
    const res = await fetch(`${API_URL}/engagement/thresholds`);
    return res.json();
  },

  async updateEngagementThreshold(paramName, value) {
    const res = await fetch(`${API_URL}/engagement/thresholds/${paramName}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value })
    });
    return res.json();
  }
};
